import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  PortfolioAllocationTableData,
  PortfolioSectorAllocationTable,
} from '@components/products/models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { LiteratureDTO, PortfolioDisplay } from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundDocumentsService } from '@products/other-details/fund-documents/services/fund-documents.service';
import filter from 'lodash/filter';
import { SiteConfigService } from "@services/site-config.service";
import { PortfolioComponentConfig } from "@products/portfolio/portfolio-component.config";
import { PortfolioService } from "@products/portfolio/portfolio.service";

import { ChartData } from '@shared/bar-chart/bar-chart.component';
import { ActivatedRoute } from "@angular/router";

const logger = Logger.getLogger('SectorBreakdownComponent');

@Component({
  selector: 'ft-sector-breakdown',
  templateUrl: './sector-breakdown.component.html',
  styleUrls: ['./sector-breakdown.component.scss'],
})
export class SectorBreakdownComponent
  extends BaseProductDetailComponent
  implements PortfolioDisplay, OnInit,AfterViewInit, OnDestroy {
  linkFromLiteratureService = {
    buttonLink: undefined,
  };
  @Input() data: PortfolioAllocationTableData;
  listOfTabs: string[] = [];
  disableTab: boolean = false;
  highestSectorAlloc: number;
  dataSubscription: Subscription[] = [];
  topTenHoldings: [];
  topTenSmallTitle: '';
  portfolioComposition: ChartData;
  asOfDate: string;
  topTenHigh: number;
  piChartPopulated: boolean;
  isTopTenPopulated: boolean;
  classForCol = 'col-lg-4 col-12'

  constructor(
    private cdr: ChangeDetectorRef,
    private fundService: FundDocumentsService,
    private siteConfig: SiteConfigService,
    private fundPortfolioService: PortfolioService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngAfterViewInit() {
    this.route.queryParams?.subscribe((queryParams) => {
      const scrollToPort = queryParams.scrolltoport;
      if (scrollToPort) {
        this.scrollToPortfolio();
      }
    })
  }

  private scrollToPortfolio() {
    const elementToScroll = document.getElementById('portfolio');
    if (elementToScroll) {
      setTimeout(() => {
        elementToScroll.scrollIntoView({behavior: "smooth", block: "start"});
      }, 3000);
    }
  }
  ngOnInit(): void {
    if (this.brConfig && this.brConfig.fundId) {
      const tabSet = this.siteConfig.getFundTabSet(this.brConfig.fundId);
      this.listOfTabs = tabSet.tabs;
      if (this.listOfTabs.includes('portfolio')) {
        this.dataSubscription.push(
          this.fundService.getFundLiteratureDocument$(
            '?channel=en-in&cache=true&frkOneTISNumber=' + this.brConfig?.fundId
          ).subscribe(
            (data: LiteratureDTO): void => {
              const buttonLink = filter(data.document, {
                contentGrouping: 'FUND-FACTSHEETS',
              });

              this.linkFromLiteratureService = {
                buttonLink: buttonLink[0]?.literatureHref
                  ? 'download' + buttonLink[0]?.literatureHref
                  : buttonLink[0]?.literatureHref,
              };
              this.cdr.detectChanges();
            },
            (err) => {
              logger.debug('Literature HTTP Error', err);
            }
          )
        );

        this.dataSubscription.push(
          this.fundPortfolioService
            .getPortfolioChartData$(
              PortfolioComponentConfig.TOP_TEN_HOLDINGS,
              this.brConfig.fundId
            )
            .subscribe((topTenData) => {
              if (topTenData && topTenData?.payload) {
                this.topTenHoldings = topTenData?.payload[0]?.dataPoints;
                this.topTenHigh = topTenData?.payload[0]?.highestPercent;
                this.topTenSmallTitle = topTenData?.payload[0]?.calculationType;
                this.asOfDate = topTenData?.payload[0].asOfDate;
                this.isTopTenPopulated = true;
              }
              this.cdr.detectChanges();
            })
        );
        this.dataSubscription.push(
          this.fundPortfolioService
            .getPortfolioChartData$(
              PortfolioComponentConfig.ASSET_ALLOCATION,
              this.brConfig.fundId
            )
            .subscribe((piChartData) => {
              //const charData: ChartData = piChartData.payload as ChartData;
              if (piChartData && piChartData.payload) {
                this.portfolioComposition = piChartData?.payload as unknown as ChartData;
                this.asOfDate = this.portfolioComposition[0]?.asOfDate;
                this.piChartPopulated = true;
              }
              this.cdr.detectChanges();
            })
        );
      } else {
        this.disableTab = true;
      }
    }
  }

  getClass() {
    if ((!this.data?.allocations && !this.isTopTenPopulated && this.piChartPopulated) || (this.data?.allocations && !this.isTopTenPopulated && !this.piChartPopulated) || (!this.data?.allocations && this.isTopTenPopulated && !this.piChartPopulated)) {
      this.classForCol = "col-lg-12 col-12";
    } else if ((!this.data?.allocations && this.isTopTenPopulated && this.piChartPopulated) || (this.data?.allocations && !this.isTopTenPopulated && this.piChartPopulated)
      || (this.data?.allocations && this.isTopTenPopulated && !this.piChartPopulated)) {
      this.classForCol = "col-lg-6 col-12";
    } else if (this.data?.allocations && this.isTopTenPopulated && this.piChartPopulated) {
      this.classForCol = "col-lg-4 col-12";
    }
    return this.classForCol;
  }

  getColumns(data: PortfolioSectorAllocationTable[], isHoldings ?: boolean) {
    let progressBarColor = ["bg-light-green", "bg-purple", "bg-primary", "bg-light-blue", "bg-light"];
    if (data) {
      for (let i = 0; i < data.length; i++) {
        data[i].progressBarColor = progressBarColor[i % 5];
        if (!isHoldings) {
          if (!this.highestSectorAlloc) { //Quick fix for current phase & may need revisit later with full portfolio development
            //let firstHighestSectorAlloc = data[i].breakdown.replace(/%/g, '');
            this.highestSectorAlloc = data[i].breakdownStd;//parseFloat(firstHighestSectorAlloc);
          }
          let currentBreakDown = data[i].breakdownStd;
          let finalPercentage = this.highestSectorAlloc === currentBreakDown ? 100 : (currentBreakDown / this.highestSectorAlloc) * 100;
          data[i].progressPercent = String(finalPercentage.toFixed(2)) + '%';
        } else if (isHoldings && this.topTenHigh) {
          let currentBreakDown = data[i].breakdownStd;
          let finalPercentage = this.topTenHigh === currentBreakDown ? 100 : (currentBreakDown / this.topTenHigh) * 100;
          data[i].progressPercent = String(finalPercentage.toFixed(2)) + '%';
        }
      }
    }
    return [data];
  }

  ngOnDestroy(): void {
    this.dataSubscription.forEach((sub) => sub?.unsubscribe());
  }
}
